@if (visible() && survey()?.display_type === 'modal') {
  <div
    class="survey-backdrop"
    (click)="dismiss()"></div>
}

@if (visible() && survey(); as s) {
  <div
    class="survey-widget"
    [class.survey-widget--modal]="s.display_type === 'modal'"
    [class.survey-widget--popup]="s.display_type === 'popup'">
    @if (submitted()) {
      <div class="survey-widget__thankyou">
        <div class="survey-widget__thankyou-icon">
          <fa-icon icon="circle-check" />
        </div>
        <h4>Thank you!</h4>
        <p>{{ s.thank_you_text }}</p>
        @if (s.coupon_code) {
          <div class="survey-widget__coupon">
            <span class="survey-widget__coupon-label">Here's your coupon code:</span>
            <code class="survey-widget__coupon-code">{{ s.coupon_code }}</code>
          </div>
        }
        @if (!s.auto_close) {
          <button
            bn-web-ui-button
            class="survey-widget__close-btn"
            itemStyle="stroke"
            size="sm"
            type="button"
            (click)="dismiss()">
            Close
          </button>
        }
      </div>
    } @else {
      <div class="survey-widget__header">
        <div class="survey-widget__header-left">
          @if (totalSteps() > 1) {
            <span class="survey-widget__step-badge">{{ currentStep() + 1 }}/{{ totalSteps() }}</span>
          }
          <h4 class="survey-widget__title">{{ s.name }}</h4>
        </div>
        <button
          class="survey-widget__close"
          title="Dismiss survey"
          (click)="dismiss()">
          <fa-icon icon="xmark" />
        </button>
      </div>

      @if (currentStepData(); as step) {
        <div class="survey-widget__body">
          @if (step.title) {
            <div class="survey-widget__step-title">{{ step.title }}</div>
          }
          @if (step.description) {
            <p class="survey-widget__step-desc">{{ step.description }}</p>
          }

          @for (field of step.fields; track field.id) {
            <div class="survey-widget__field">
              <label class="survey-widget__field-label">
                {{ field.label }}
                @if (field.is_required) {
                  <span class="survey-widget__required">*</span>
                }
              </label>
              @if (field.description) {
                <p class="survey-widget__field-desc">{{ field.description }}</p>
              }

              @switch (field.field_type) {
                @case ('rating') {
                  @switch (getRatingStyle(field)) {
                    @case ('smileys') {
                      <div class="survey-widget__rating survey-widget__rating--smileys">
                        @for (smiley of smileyIcons; track smiley.value) {
                          <button
                            class="survey-widget__rating-btn survey-widget__rating-btn--smiley"
                            type="button"
                            [class.survey-widget__rating-btn--active]="getAnswer(field.id) === smiley.value"
                            [title]="smiley.label"
                            (click)="setAnswer(field.id, smiley.value)">
                            <fa-icon [icon]="smiley.icon" />
                          </button>
                        }
                      </div>
                    }
                    @case ('thumbs') {
                      <div class="survey-widget__rating survey-widget__rating--thumbs">
                        <button
                          class="survey-widget__rating-btn survey-widget__rating-btn--thumb"
                          type="button"
                          [class.survey-widget__rating-btn--active]="getAnswer(field.id) === '1'"
                          (click)="setAnswer(field.id, '1')">
                          <fa-icon icon="thumbs-down" />
                        </button>
                        <button
                          class="survey-widget__rating-btn survey-widget__rating-btn--thumb"
                          type="button"
                          [class.survey-widget__rating-btn--active]="getAnswer(field.id) === '2'"
                          (click)="setAnswer(field.id, '2')">
                          <fa-icon icon="thumbs-up" />
                        </button>
                      </div>
                    }
                    @default {
                      <div class="survey-widget__rating">
                        @for (n of getRatingScale(field); track n) {
                          <button
                            class="survey-widget__rating-btn"
                            type="button"
                            [class.survey-widget__rating-btn--active]="getAnswer(field.id) === '' + n"
                            (click)="setAnswer(field.id, '' + n)">
                            {{ n }}
                          </button>
                        }
                      </div>
                    }
                  }
                }

                @case ('input') {
                  <textarea
                    class="survey-widget__input"
                    rows="3"
                    [class.survey-widget__input--error]="isFieldInvalid(field.id, field.is_required)"
                    [ngModel]="getAnswer(field.id) || ''"
                    [placeholder]="field.description || 'Type your answer...'"
                    (ngModelChange)="setAnswer(field.id, $event)"></textarea>
                }

                @case ('single_choice') {
                  <div class="survey-widget__choices">
                    @for (option of field.options; track option) {
                      <label
                        class="survey-widget__choice"
                        [class.survey-widget__choice--selected]="getAnswer(field.id) === option">
                        <input
                          type="radio"
                          [checked]="getAnswer(field.id) === option"
                          [name]="field.id"
                          [value]="option"
                          (change)="setAnswer(field.id, option)" />
                        <span>{{ option }}</span>
                      </label>
                    }
                  </div>
                }

                @case ('multi_choice') {
                  <bn-web-ui-checkbox-list
                    columnsCount="1"
                    [items]="getCheckboxItems(field)"
                    [ngModel]="getCheckboxValue(field.id)"
                    (ngModelChange)="onCheckboxChange(field.id, $event)" />
                }
              }
            </div>
          }
        </div>
      }

      <!-- Progress dots + footer -->
      @if (totalSteps() > 1) {
        <div class="survey-widget__progress">
          <div class="survey-widget__progress-bar">
            <div
              class="survey-widget__progress-fill"
              [style.width.%]="((currentStep() + 1) / totalSteps()) * 100"></div>
          </div>
        </div>
      }

      <div class="survey-widget__footer">
        <div class="survey-widget__footer-left">
          @if (currentStep() > 0) {
            <button
              bn-web-ui-button
              itemStyle="stroke"
              size="sm"
              type="button"
              (click)="prevStep()">
              <fa-icon
                class="me-1"
                icon="arrow-left" />
              Back
            </button>
          }
        </div>
        <div class="survey-widget__footer-right">
          @if (currentStep() < totalSteps() - 1) {
            <button
              bn-web-ui-button
              size="sm"
              type="button"
              (click)="nextStep()">
              Next
              <fa-icon
                class="ms-1"
                icon="arrow-right" />
            </button>
          } @else {
            <button
              bn-web-ui-button
              size="sm"
              type="button"
              [loading]="submitting()"
              (click)="submit()">
              Submit
            </button>
          }
        </div>
      </div>
    }
  </div>
}
